import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ConsolidatedKey, ConsolidatedReport, Debtor } from '../types';
import { api } from '../services/api';
import { formatMoney } from '../utils/format';

type Row = { label: string; keys: [ConsolidatedKey] | [ConsolidatedKey, ConsolidatedKey]; bold?: boolean; hint?: string };
// Строки сводной таблицы (как в Excel): налоги посчитаны по каждой организации отдельно, «Итого» — сумма
const ROWS: Row[] = [
    { label: 'Выручка (безнал / нал)', keys: ['revenue_bank', 'revenue_cash'] },
    { label: 'Прочие поступления (не доход)', keys: ['other_in'], hint: 'Займы от третьих лиц, возвраты, личные средства. Перемещения между счетами и кошельками и переводы между ИП не входят' },
    { label: 'Расходы (безнал / нал)', keys: ['cash_out_bank', 'cash_out_cash'] },
    { label: 'Зарплата (офиц. / неофиц.)', keys: ['salary_official', 'salary_unofficial'] },
    { label: 'Налоги начислено / уплачено', keys: ['taxes_accrued', 'taxes_paid'], hint: 'Начислено — по уведомлениям, где они введены, иначе расчёт приложения' },
    { label: 'Прибыль до изъятий', keys: ['net_profit'] },
    { label: 'Баланс (все кошельки)', keys: ['balance'], bold: true },
    { label: 'Сальдо ЕНС', keys: ['ens_wallet'] },
    { label: 'Налоги к уплате', keys: ['unpaid_taxes'] },
    { label: 'Резерв ИП (1% поступлений)', keys: ['reserve'] },
    { label: 'Можно забрать', keys: ['available'], bold: true },
];

const cell = (values: Record<ConsolidatedKey, number>, keys: Row['keys']) =>
    keys.length === 1 ? formatMoney(values[keys[0]]) : `${formatMoney(values[keys[0]])} / ${formatMoney(values[keys[1]])}`;

export const ConsolidatedDashboard: React.FC<{ year: number; revision: number; orgNames: Record<string, string> }> = ({ year, revision, orgNames }) => {
    const [report, setReport] = useState<ConsolidatedReport | null>(null);
    useEffect(() => { api.getConsolidatedReport(year).then(setReport).catch(() => setReport(null)); }, [year, revision]);
    if (!report) return <div className="bg-white rounded-xl border border-slate-200 p-6 text-sm text-slate-400">Загрузка сводки…</div>;
    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600">
                        <tr>
                            <th className="text-left px-3 py-2">Показатель ({year})</th>
                            {report.organizations.map(o => <th key={o.id} className="text-right px-3 py-2 whitespace-nowrap">{o.name}</th>)}
                            <th className="text-right px-3 py-2 bg-slate-100">Итого</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {ROWS.map(r => (
                            <tr key={r.label} className={r.bold ? 'font-bold' : ''}>
                                <td className="px-3 py-1.5 text-slate-700">{r.label}</td>
                                {report.organizations.map(o => <td key={o.id} className="px-3 py-1.5 text-right font-mono whitespace-nowrap">{cell(o.values, r.keys)}</td>)}
                                <td className={`px-3 py-1.5 text-right font-mono whitespace-nowrap bg-slate-50 ${r.keys[0] === 'available' ? (report.totals.available >= 0 ? 'text-emerald-700' : 'text-red-600') : ''}`}>{cell(report.totals, r.keys)}</td>
                            </tr>
                        ))}
                        <tr>
                            <td className="px-3 py-1.5 text-slate-500 align-top">Кошельки</td>
                            {report.organizations.map(o => (
                                <td key={o.id} className="px-3 py-1.5 text-right text-[10px] text-slate-500 align-top">
                                    {o.wallets.map(w => <div key={w.id} className="whitespace-nowrap">{w.name}: <span className="font-mono">{formatMoney(w.balance)}</span></div>)}
                                </td>
                            ))}
                            <td className="bg-slate-50"></td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className="px-3 py-2 border-t border-slate-100 text-[11px] text-slate-500">
                Налоги считаются по каждой организации отдельно (свои лимиты, 1%, 50%, НДС), в «Итого» — только сумма. Переводы между организациями не входят в приходы и расходы.
            </div>
            {report.debts.length > 0 && (
                <div className="px-3 py-2 border-t border-slate-100 text-xs">
                    <span className="font-bold text-slate-600">Взаимные долги: </span>
                    {report.debts.map((d, i) => <span key={i} className="mr-3">{orgNames[d.borrowerOrgId] || d.borrowerOrgId} должен {orgNames[d.lenderOrgId] || d.lenderOrgId} <strong>{formatMoney(d.amount)}</strong></span>)}
                </div>
            )}
        </div>
    );
};

// Виджет «Нам должны»: сумма, топ-5 должников и ожидаемые поступления по графику оплат
export const DebtorsWidget: React.FC<{ revision: number; scope?: string }> = ({ revision, scope }) => {
    const [data, setData] = useState<{ items: Debtor[]; total: number } | null>(null);
    const [schedule, setSchedule] = useState<{ next14: number; overdue: number } | null>(null);
    useEffect(() => {
        let alive = true;
        api.getDebtors(scope).then(d => { if (alive) setData(d); }).catch(() => { if (alive) setData(null); });
        api.getSchedule(scope).then(s => { if (alive) setSchedule(s); }).catch(() => { if (alive) setSchedule(null); });
        return () => { alive = false; };
    }, [revision, scope]);
    if ((!data || data.total <= 0) && !schedule?.next14 && !schedule?.overdue) return null;
    return (
        <div className="bg-white rounded-xl border border-amber-200 p-3 text-xs">
            <div className="flex justify-between items-center mb-1">
                <span className="font-bold text-amber-800">Нам должны: {formatMoney(data?.total || 0)}</span>
                <Link to="/projects?tab=debtors" className="text-blue-600 hover:underline">Кто нам должен →</Link>
            </div>
            {schedule && (schedule.next14 > 0 || schedule.overdue > 0) && (
                <Link to="/projects?tab=schedule" className="block mb-1 text-slate-600 hover:underline">
                    Ожидается в ближайшие 14 дней: <strong className="text-slate-900">{formatMoney(schedule.next14)}</strong>
                    {' · '}Просрочено: <strong className={schedule.overdue > 0 ? 'text-red-600' : 'text-slate-900'}>{formatMoney(schedule.overdue)}</strong>
                </Link>
            )}
            {(data?.items || []).filter(d => d.debt > 0).slice(0, 5).map(d => (
                <div key={d.customerId || d.customerName} className="flex justify-between gap-2 py-0.5">
                    <span className="truncate">{d.customerName}{d.oldestDays !== null ? <span className="text-slate-400"> · счёт {d.oldestDays} дн.</span> : null}</span>
                    <span className="font-mono">{formatMoney(d.debt)}</span>
                </div>
            ))}
        </div>
    );
};

